import { useRef, useState, type RefObject, type LegacyRef } from 'react'
import { Send, Paperclip, X, FileText, Image as ImageIcon, FileSpreadsheet, File } from 'lucide-react'
import clsx from 'clsx'
import { track } from '@ytest/ui'

export function getFileIcon(type: string) {
  if (type.startsWith('image/')) return ImageIcon
  if (type === 'application/pdf' || type.includes('word')) return FileText
  if (type.includes('csv') || type.includes('spreadsheet') || type.includes('excel')) return FileSpreadsheet
  return File
}

export function formatSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

const ACCEPTED_TYPES = '.pdf,.txt,.csv,.json,.md,.xml,.html,.png,.jpg,.jpeg,.gif,.webp,.doc,.docx,.xls,.xlsx,.log,.yaml,.yml,.rtf,.py,.js,.ts,.sql,.sh,.java,.go,.rb,.php,.css,.toml,.ini,.cfg,.env'
const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10 MB

export interface ChatInputProps {
  input: string
  onInputChange: (value: string) => void
  onKeyDown: (e: React.KeyboardEvent) => void
  onSend: () => void
  inputRef: RefObject<HTMLTextAreaElement | null>
  attachedFile: File | null
  onAttachFile: (file: File | null) => void
  isTyping?: boolean
  onStopGenerating?: () => void
}

export function ChatInput({
  input,
  onInputChange,
  onKeyDown,
  onSend,
  inputRef,
  attachedFile,
  onAttachFile,
  isTyping = false,
  onStopGenerating,
}: ChatInputProps) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [fileError, setFileError] = useState<string | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const dragCounterRef = useRef(0)

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    dragCounterRef.current++
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true)
    }
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    dragCounterRef.current--
    if (dragCounterRef.current === 0) {
      setIsDragging(false)
    }
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
    dragCounterRef.current = 0

    const file = e.dataTransfer.files?.[0]
    if (!file) return

    setFileError(null)
    if (file.size > MAX_FILE_SIZE) {
      setFileError('File must be under 10 MB')
      return
    }
    onAttachFile(file)
    track('TestOps File Attached', { method: 'drag_drop', fileType: file.type, fileSize: file.size })
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFileError(null)
    const file = e.target.files?.[0]
    if (!file) return

    if (file.size > MAX_FILE_SIZE) {
      setFileError('File must be under 10 MB')
      return
    }
    onAttachFile(file)
    track('TestOps File Attached', { method: 'button', fileType: file.type, fileSize: file.size })
    // Reset so the same file can be re-selected
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleRemoveFile = () => {
    onAttachFile(null)
    setFileError(null)
  }

  const canSend = input.trim() || attachedFile

  return (
    <div
      className="border-t border-subtle bg-card relative"
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {/* Drag overlay */}
      {isDragging && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-accent-soft/90 border-2 border-dashed border-accent/50 rounded-lg m-2">
          <div className="text-center">
            <Paperclip className="w-8 h-8 text-accent mx-auto mb-2" />
            <p className="text-sm font-medium text-accent-soft-foreground">Drop file here</p>
          </div>
        </div>
      )}
      <div className="max-w-3xl mx-auto p-4">
        {/* Attached file preview */}
        {attachedFile && (
          <div className="mb-2 flex items-center gap-2 px-3 py-2 bg-accent-soft border border-accent/30 rounded-lg">
            {(() => {
              const Icon = getFileIcon(attachedFile.type)
              return <Icon className="w-4 h-4 text-accent shrink-0" />
            })()}
            <span className="text-sm text-accent truncate flex-1">{attachedFile.name}</span>
            <span className="text-xs text-accent shrink-0">{formatSize(attachedFile.size)}</span>
            <button
              onClick={handleRemoveFile}
              className="p-0.5 hover:bg-accent/15 rounded transition shrink-0"
            >
              <X className="w-3.5 h-3.5 text-accent" />
            </button>
          </div>
        )}

        {fileError && (
          <p className="mb-2 text-xs text-destructive px-3">{fileError}</p>
        )}

        <div className="relative flex items-center">
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_TYPES}
            onChange={handleFileSelect}
            className="hidden"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            className="absolute left-3 p-1.5 hover:bg-foreground/[0.06] rounded-lg transition"
            title="Attach a file"
          >
            <Paperclip className="w-5 h-5 text-subtle-foreground" />
          </button>
          <textarea
            ref={inputRef as LegacyRef<HTMLTextAreaElement>}
            value={input}
            onChange={(e) => {
              onInputChange(e.target.value)
              if (inputRef.current) {
                inputRef.current.style.height = 'auto'
                inputRef.current.style.height = `${Math.min(inputRef.current.scrollHeight, 200)}px`
              }
            }}
            onKeyDown={onKeyDown}
            onPaste={(e) => {
              const items = e.clipboardData?.items
              if (!items) return
              for (const item of items) {
                if (item.type.startsWith('image/')) {
                  e.preventDefault()
                  const file = item.getAsFile()
                  if (file) {
                    setFileError(null)
                    if (file.size > MAX_FILE_SIZE) {
                      setFileError('Pasted image must be under 10 MB')
                      return
                    }
                    onAttachFile(file)
                    track('TestOps File Attached', { method: 'paste', fileType: file.type, fileSize: file.size })
                  }
                  break
                }
              }
            }}
            placeholder="Message TestOps Command..."
            aria-label="Message input"
            className="w-full pl-12 pr-12 py-3 bg-foreground/[0.04] border rounded-2xl resize-none outline-none focus:ring-2 focus:ring-ring focus:border-transparent text-sm text-foreground placeholder:text-muted-foreground overflow-y-auto transition-all"
            rows={1}
            style={{ minHeight: '44px', maxHeight: '200px' }}
            autoFocus
          />
          {isTyping ? (
            <button
              onClick={onStopGenerating}
              className="absolute right-2 h-9 w-9 flex items-center justify-center rounded-full transition bg-foreground/[0.06] border hover:bg-foreground/[0.10]"
              aria-label="Stop generating"
            >
              <div className="h-3.5 w-3.5 rounded-sm bg-foreground/40" />
            </button>
          ) : (
            <button
              onClick={onSend}
              disabled={!canSend}
              className={clsx(
                'absolute right-2 p-2 rounded-lg transition-all',
                canSend
                  ? 'bg-accent text-white hover:bg-accent/90 shadow-md shadow-accent/30 hover:shadow-lg hover:shadow-accent/40'
                  : 'bg-foreground/[0.10] text-subtle-foreground cursor-not-allowed',
              )}
              aria-label="Send message"
            >
              <Send className="w-4 h-4" />
            </button>
          )}
        </div>
        <p className="font-mono text-[11px] text-subtle-foreground text-center mt-2">
          TestOps Command can make mistakes. Consider checking important information.
        </p>
      </div>
    </div>
  )
}
